/**
 * Plan 34 slice 3 — the WORLD RECORD browsing surface.
 *
 * One panel, created once: the start-screen entry opens it and the in-game "Other game
 * modes" details reuses the same opener. Everything network-shaped flows through slice 2's
 * `createLeaderboardClient` — one fetch path, one 60 s cache layer; this file only renders,
 * and the API/board contract never throws into the game.
 */

import { FAILURE_COPY, TOP_CAP, createLeaderboardClient, createPlayerStore, createRecordStore, invalidateBoard, type Board, type BoardEntry, type LeaderboardClient, type PlayerStore, type RecordStore } from './leaderboard';
import './leaderboard.css';

const node = (tag: string, text = '', cls = '') => { const el = document.createElement(tag); el.textContent = text; el.className = cls; return el; };
const button = (text: string, action: () => void) => { const b = document.createElement('button'); b.type = 'button'; b.textContent = text; b.onclick = action; return b; };
const n = (v: number) => v.toLocaleString('en-US');

export interface LeaderboardPanelDeps {
  client(): LeaderboardClient;
  players: PlayerStore;
  /** The last submission THIS browser got accepted — the own-row key (null: never submitted). */
  records: RecordStore;
  /** Local best (the Last Light save), footer only. */
  best(): number | null;
}

export function defaultPanelDeps(): LeaderboardPanelDeps {
  let client: LeaderboardClient | null = null;
  return {
    client: () => (client ??= createLeaderboardClient()),
    players: createPlayerStore(),
    records: createRecordStore(),
    best: () => null,
  };
}

/** The board carries no client ids (privacy), so the own row is matched by the remembered submission. */
function ownEntry(board: Board, records: RecordStore): BoardEntry | null {
  const record = records.read();
  if (!record) return null;
  return board.top.find(e => e.name === record.name && e.score === record.score && e.depth === record.depth)
    ?? (board.top[record.rank - 1]?.name === record.name && board.top[record.rank - 1]?.score === record.score ? board.top[record.rank - 1]! : null)
    ?? null;
}

function row(entry: BoardEntry, own: boolean) {
  const li = node('li', '', `leaderboard-row${own ? ' leaderboard-own' : ''}`);
  if (own) li.setAttribute('aria-current', 'true');
  li.append(
    node('span', `#${entry.rank}`, 'leaderboard-rank'),
    node('span', entry.name, 'leaderboard-name'),
    node('span', n(entry.score), 'leaderboard-score'),
    node('span', `M${entry.depth}`, 'leaderboard-depth'),
    node('span', entry.outcome, 'leaderboard-outcome'),
  );
  return li;
}

/** Eight grey rows while the board loads — motion-free under prefers-reduced-motion (css). */
function skeleton() {
  const ol = node('ol', '', 'leaderboard-rows leaderboard-skeleton');
  ol.setAttribute('aria-label', 'Loading the world record');
  ol.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < 8; i++) ol.append(node('li', '', 'leaderboard-row'));
  return ol;
}

export function createLeaderboardPanel(overrides: Partial<LeaderboardPanelDeps> = {}) {
  const deps: LeaderboardPanelDeps = { ...defaultPanelDeps(), ...overrides };
  const panel = node('div', '', 'expedition-overlay leaderboard-overlay');
  panel.id = 'leaderboard-panel';
  panel.hidden = true;
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-label', 'World record — top operations');
  const content = node('div', '', 'expedition-content leaderboard-content');
  panel.append(content);
  document.body.append(panel);
  let opener: HTMLElement | null = null;
  let loading = false;

  // Same furniture as the light panel's handler (lastLightUi): Tab cycles the visible
  // controls, Escape closes and hands focus back to the entry that opened the panel.
  panel.onkeydown = e => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    const bs = [...panel.querySelectorAll<HTMLElement>('button:not(:disabled)')].filter(b => b.getClientRects().length > 0);
    if (!bs.length) return;
    if (e.shiftKey && document.activeElement === bs[0]) { e.preventDefault(); bs.at(-1)?.focus(); }
    else if (!e.shiftKey && document.activeElement === bs.at(-1)) { e.preventDefault(); bs[0]?.focus(); }
  };

  function close() { panel.hidden = true; opener?.focus(); }

  function open(from?: HTMLElement) {
    if (from) opener = from;
    if (!panel.hidden) return;
    panel.hidden = false;
    void load();
  }

  async function load() {
    if (loading) return;
    loading = true;
    const live = node('p', '', 'leaderboard-status');
    live.setAttribute('aria-live', 'polite');
    live.textContent = 'Loading the world record…';
    content.replaceChildren(
      node('h2', 'WORLD RECORD', 'expedition-title'),
      node('p', 'TOP OPERATIONS — every accepted run, ranked by score.', 'expedition-intro'),
      skeleton(),
    );
    content.append(live);
    content.querySelector<HTMLButtonElement>('button')?.focus();
    const board = await deps.client().board(TOP_CAP);
    loading = false;
    render(board, live);
  }

  function render(board: Board | null, live: HTMLElement) {
    content.replaceChildren(
      node('h2', 'WORLD RECORD', 'expedition-title'),
      node('p', 'TOP OPERATIONS — every accepted run, ranked by score.', 'expedition-intro'),
    );
    if (board && board.top.length) {
      const own = ownEntry(board, deps.records);
      const rows = node('ol', '', 'leaderboard-rows');
      for (const entry of board.top) rows.append(row(entry, entry === own));
      live.textContent = `Top ${board.top.length} of ${n(board.total)} logged operations.`;
      const foot = node('div', '', 'leaderboard-foot');
      const record = deps.records.read();
      const best = deps.best();
      foot.append(node('span', best !== null ? `LOCAL BEST ${n(best)}` : 'NO LOCAL RECORD YET'));
      foot.append(node('span', record ? `RANK #${n(record.rank)} OF ${n(record.total)}` : 'NOT ON THE BOARD YET'));
      const actions = node('div', '', 'leaderboard-actions');
      actions.append(button('REFRESH', () => { invalidateBoard(); void load(); }), button('CLOSE', close));
      content.append(rows, foot, live, actions);
    } else if (board) {
      live.textContent = 'No operations logged yet — finish a Last Light run and be the first.';
      content.append(node('p', 'The board is empty. Your submission will be rank #1.', 'expedition-intro'), live, button('CLOSE', close));
    } else {
      // Every failure class folds into the one pinned copy (offline, 503, malformed).
      live.textContent = FAILURE_COPY;
      const actions = node('div', '', 'leaderboard-actions');
      actions.append(button('RETRY', () => { invalidateBoard(); void load(); }), button('CLOSE', close));
      content.append(node('p', 'The world record could not be reached. Your records stay in this browser.', 'expedition-intro'), live, actions);
    }
    content.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
  }

  return { open, close, panel };
}

/** The start-screen entry — same furniture as the Last Light entry, mounted beside it. */
export function mountLeaderboardEntry(open: (from: HTMLElement) => void) {
  const start = document.getElementById('start');
  if (!start || start.querySelector('.leaderboard-entry')) return;
  const entry = node('section', '', 'expedition-entry leaderboard-entry');
  const browse = button('BROWSE TOP 50', () => open(browse));
  entry.append(
    node('h2', 'WORLD RECORD'),
    node('p', 'The global top operations, ranked by score. Anonymous callsigns; your record stays in this browser.', 'expedition-entry-hint'),
    browse,
  );
  start.prepend(entry);
}
